// src/pages/BulkUrlProcessor.js
import React, { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";

function BulkUrlProcessor() {
  // ---------------------------------------------------------------------------
  // 1. Retrieve User ID from Local Storage
  // ---------------------------------------------------------------------------
  const authDataString = localStorage.getItem("authData");
  const parsedAuthData = authDataString ? JSON.parse(authDataString) : null;
  const userId = parsedAuthData?.user?.sub;

  // ---------------------------------------------------------------------------
  // 2. State for Favorites, Selected Favorite, and Errors
  // ---------------------------------------------------------------------------
  const [favoriteDrafts, setFavoriteDrafts] = useState([]);
  const [selectedFavorite, setSelectedFavorite] = useState("");
  const [favoritesError, setFavoritesError] = useState("");

  // ---------------------------------------------------------------------------
  // 3. State for Bulk URL Processing
  // ---------------------------------------------------------------------------
  const [urlsInput, setUrlsInput] = useState("");
  const [jobs, setJobs] = useState([]);
  const [bulkError, setBulkError] = useState("");
  const [isBulkLoading, setIsBulkLoading] = useState(false);

  // ---------------------------------------------------------------------------
  // 4. Fetch Favorite Drafts from API Gateway
  // ---------------------------------------------------------------------------
  const fetchFavorites = useCallback(async () => {
    if (!userId) return;
    try {
      const FAVORITES_API_URL =process.env.REACT_APP_FAVORITES_URL;
        
      const payload = { userId };
      console.debug("Fetching favorites with payload:", payload);

      const response = await fetch(FAVORITES_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      console.debug("Favorites API response:", response);

      if (!response.ok) {
        throw new Error("Failed to fetch favorite drafts.");
      }

      const data = await response.json();
      console.debug("Raw favorites response data:", data);
      // Expecting data.body to be a JSON-stringified array
      const parsedData = data.body ? JSON.parse(data.body) : [];
      console.debug("Parsed favorites data:", parsedData);

      // Format the drafts (adjust keys as needed)
      const formattedFavorites = parsedData.map((item) => ({
        draftId: item.id,
        nickname: item.nickName,
        draft: item.draft,
        isDefault: item.isDefault === "Y",
      }));

      console.debug("Formatted favorites:", formattedFavorites);
      setFavoriteDrafts(formattedFavorites);
    } catch (error) {
      console.error("Error fetching favorites:", error);
      setFavoritesError(error.message || "Error fetching favorites");
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      fetchFavorites();
    }
  }, [userId, fetchFavorites]);

  // ---------------------------------------------------------------------------
  // 5. Helper Function: Format Draft Message
  // ---------------------------------------------------------------------------
  const formatDraftMessage = (message) => {
    if (!message) return null;

    // Split message into non-empty lines
    const lines = message
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    if (lines.length === 0) return null;

    // The first line is assumed to be the Subject.
    const subjectLine = lines[0];

    // Attempt to find a signature start in the remaining lines.
    const signatureRegex = /^(Best regards,|Regards,|Sincerely,|Thank you,)/i;
    let signatureIndex = lines.findIndex(
      (line, index) => index > 0 && signatureRegex.test(line)
    );

    // If a signature is found, separate body and signature.
    const bodyLines =
      signatureIndex > 0 ? lines.slice(1, signatureIndex) : lines.slice(1);
    const signatureLines = signatureIndex > 0 ? lines.slice(signatureIndex) : [];

    return (
      <div className="whitespace-pre-line">
        {/* Subject */}
        <h3 className="font-bold text-lg mb-2">{subjectLine}</h3>
        {/* Body paragraphs */}
        {bodyLines.map((line, index) => (
          <p key={index} className="mb-2">
            {line}
          </p>
        ))}
        {/* Signature block */}
        {signatureLines.length > 0 && (
          <div className="mt-4 border-t pt-2 italic text-gray-700">
            {signatureLines.map((line, index) => (
              <p key={index} className="mb-1">
                {line}
              </p>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // 6. Bulk URL Processing Submission
  // ---------------------------------------------------------------------------
  const BULK_API_ENDPOINT =process.env.REACT_APP_BULK_PERSONALIZE_URL;
    

  const generateJobId = () => {
    return "job-" + Date.now() + "-" + Math.floor(Math.random() * 1000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBulkError("");

    // Ensure both userId and selectedFavorite (as draftId) are available.
    if (!userId) {
      setBulkError("User not authenticated. Please log in again.");
      return;
    }
    if (!selectedFavorite) {
      setBulkError("Please select a draft from favorites.");
      return;
    }

    // Split input into an array, trim whitespace, and remove blank lines.
    const urls = urlsInput
      .split("\n")
      .map((url) => url.trim())
      .filter((url) => url !== "");
    console.debug("Submitting URLs:", urls);

    if (urls.length === 0) {
      setBulkError("Please enter at least one URL.");
      return;
    }
    if (urls.length > 20) {
      setBulkError("Please enter no more than 20 URLs.");
      return;
    }

    const jobId = generateJobId();
    const newJob = {
      jobId,
      submitted: new Date().toLocaleString(),
      status: "Processing",
      downloadUrl: null,
    };

    console.debug("New job created:", newJob);
    setJobs((prevJobs) => [newJob, ...prevJobs]);

    setIsBulkLoading(true);
    try {
      console.debug("Calling bulk API at:", BULK_API_ENDPOINT);

      // Construct the payload with a nested "body" property as a JSON string.
      const payload = {
        body: JSON.stringify({
          draftId: selectedFavorite,
          userId,
          urls,
        }),
      };
      console.debug("Payload sent to bulk API:", payload);

      const response = await fetch(BULK_API_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      console.debug("Bulk API response:", response);

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      // Parse the lambda response.
      const responseData = await response.json();
      console.debug("Raw bulk API response data:", responseData);

      // The lambda returns the array as a JSON-string in the "body" field.
      let dataArray = responseData.body
        ? JSON.parse(responseData.body)
        : responseData;
      console.debug("Parsed bulk API data:", dataArray);

      // If dataArray is not an array, wrap it inside an array.
      if (!Array.isArray(dataArray)) {
        dataArray = [dataArray];
      }
      console.debug("Final bulk API data array:", dataArray);

      // Map through the data to create file content.
      const fileContent = dataArray
        .map((item) => `${item.linkedinUrl} | ${item.personalizedDraft}`)
        .join("\n");
      console.debug("Generated file content:", fileContent);

      // Create a blob for the downloadable text file.
      const blob = new Blob([fileContent], { type: "text/plain" });
      const downloadUrl = URL.createObjectURL(blob);
      console.debug("Download URL created:", downloadUrl);

      // Update the job with a completed status and the download URL.
      setJobs((prevJobs) =>
        prevJobs.map((job) =>
          job.jobId === jobId ? { ...job, status: "Completed", downloadUrl } : job
        )
      );
    } catch (err) {
      console.error("Error during bulk processing:", err);
      // Update the job status to error.
      setJobs((prevJobs) =>
        prevJobs.map((job) =>
          job.jobId === jobId ? { ...job, status: "Error" } : job
        )
      );
      setBulkError("Error processing the job: " + err.message);
    } finally {
      setIsBulkLoading(false);
    }
  };

  return (
    <Layout>
      <div className="container mx-auto mt-10 max-w-4xl bg-white shadow-md p-10 rounded-md">
        <h1 className="text-xl font-semibold mb-6">
          Personalize Messages for Linkedin Profiles
        </h1>

        {/* Display any errors */}
        {favoritesError && (
          <p className="text-red-500 mb-4">Favorites: {favoritesError}</p>
        )}
        {bulkError && <p className="text-red-500 mb-4">{bulkError}</p>}

        {/* Favorites Dropdown */}
        {favoriteDrafts.length > 0 && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select a Draft From Favorites
            </label>
            <p className="text-sm text-gray-500 italic">
              (See All Drafts List and check the box to include a Draft in Favorites)
            </p>
            <select
              className="w-full p-2 border rounded-md"
              value={selectedFavorite}
              onChange={(e) => setSelectedFavorite(e.target.value)}
            >
              <option value="">-- Select a Favorite --</option>
              {favoriteDrafts.map((fav) => (
                <option key={fav.draftId} value={fav.draftId}>
                  {fav.nickname} {fav.isDefault && "(Default)"}
                </option>
              ))}
            </select>
            {selectedFavorite && (
              <div className="mt-4 p-2 border rounded-md bg-gray-50">
                {formatDraftMessage(
                  favoriteDrafts.find((fav) => fav.draftId === selectedFavorite)
                    ?.draft
                )}
              </div>
            )}
          </div>
        )}

        {/* Bulk URL Submission Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="urls"
              className="block text-sm font-medium text-gray-700"
            >
              Paste up to 20 URLs (one per line):
            </label>
            <textarea
              id="urls"
              value={urlsInput}
              onChange={(e) => setUrlsInput(e.target.value)}
              rows="6"
              className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="https://linkedin.com/in/example1&#10;https://linkedin.com/in/example2"
            ></textarea>
          </div>
          <div>
            <button
              type="submit"
              disabled={isBulkLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none disabled:bg-indigo-400"
            >
              Generate Bulk Drafts
            </button>
          </div>
        </form>

        {/* Job Status Table */}
        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-4">Result</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Task
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date/Time
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {jobs.length === 0 ? (
                  <tr>
                    <td
                      colSpan="4"
                      className="px-4 py-2 text-center text-sm text-gray-500"
                    >
                      No jobs submitted yet.
                    </td>
                  </tr>
                ) : (
                  jobs.map((job) => (
                    <tr key={job.jobId}>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {job.jobId}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {job.submitted}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {job.status === "Processing" ? (
                          <span className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-xs font-medium bg-gray-200 cursor-wait">
                            Processing...
                          </span>
                        ) : job.status === "Completed" ? (
                          <span className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-xs font-medium bg-green-200">
                            Completed
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-xs font-medium bg-red-200">
                            Error
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {job.downloadUrl ? (
                          <a
                            href={job.downloadUrl}
                            download={`output_${job.jobId}.txt`}
                            className="text-indigo-600 hover:text-indigo-900 font-medium"
                          >
                            Download
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default BulkUrlProcessor;
